import { jsonLanguage } from "@codemirror/lang-json";
import { Button } from "@totalenergiescode/mobility-business-rev-design-system";
import {
  CardProWiringProviderInterface,
  useFormDataContext,
} from "@totalenergiescode/mobility-business-rev-form-data/components";
import { SubmittedData } from "@totalenergiescode/mobility-business-rev-form-data/lib";
import { githubLight } from "@uiw/codemirror-theme-github";
import ReactCodeMirror from "@uiw/react-codemirror";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  StyledRenderContainer,
  StyledTabMenuContainer,
} from "@/components/BackOffice/styles";
import { RoundedTopLeftContainer } from "@/components/containers/RoundedTopLeftContainer";
import AnomaliesViewer from "@/components/FormData/debug/anomalies/AnomaliesViewer";
import FormDataRenderer from "@/components/FormData/lib/layout/FormDataRenderer";
import StepperComponent from "@/components/FormData/lib/layout/Stepper/StepperComponent";
import {
  StyledContainer,
  StyledEmptyForm,
  StyledJsonPanel,
  StyledLoadButton,
  StyledPageHeader,
  StyledPanelsContainer,
  StyledRenderPanel,
  StyledTemplateSelector,
  StyledTemplateSelectorButtons,
  StyledTemplateSelectorLabel,
} from "@/components/FormData/styles";
import CreationTemplate from "@/components/FormData/templates/CreationTemplate/component";
import HardCodedTemplate from "@/components/FormData/templates/HardCodedTemplate/component";
import ModificationTemplate from "@/components/FormData/templates/ModificationTemplate/component";
import { TabMenuButton } from "@/components/shared/TabMenuButton";

const FormData = ({
  cardProProviders,
}: {
  cardProProviders: CardProWiringProviderInterface;
}) => {
  const { t: tCommon } = useTranslation("common");
  const { t: tFormData } = useTranslation("formData");
  const [isCreateTemplateOpen, setIsCreateTemplateOpen] = useState(false);
  const [isModificationTemplateOpen, setIsModificationTemplateOpen] =
    useState(false);
  const [selectedTab, setTabSelected] = useState(0);
  const [isHardcodedTemplateOpen, setIsHardcodedTemplateOpen] = useState(false);
  const [json, setJson] = useState("");

  const { formHasBeenLoaded, load, builderOptions, form } =
    useFormDataContext();

  const onTemplateSelected = (json: string): void => {
    setJson(json);
  };

  const onLoad = () => {
    load(json, builderOptions, null);
  };

  const breadcrumbItems = [
    {
      title: tCommon("common.features.dashboard"),
      path: "/" as const,
    },
    {
      title: tCommon("common.sidebar.formData"),
      path: "/formdata" as const,
    },
  ];

  const tabs = [
    {
      label: tFormData("formData.render.title"),
      dataTestId: "tab-menu-button",
      name: "render",
      hasBubbleText: false,
      variant: "grey",
      onClick: () => {},
    },
    {
      label: tFormData("formData.anomalies.title"),
      dataTestId: "tab-menu-button",
      name: "anomalies",
      hasBubbleText: formHasBeenLoaded && form.anomaliesReport().count(),
      variant: "red",
      text: form.anomaliesReport().count(),
      onClick: () => {},
    },
  ];

  const showRender = selectedTab === 0;
  const showAnomalies = selectedTab === 1;

  async function handleSubmitFormdata(data: SubmittedData) {
    console.log("submitted data >>>", data.dump());
  }

  return (
    <StyledContainer>
      <StyledPageHeader
        breadcrumbItems={breadcrumbItems}
        title={tCommon("common.sidebar.formData")}
        href=''
      />

      <CreationTemplate
        isOpen={isCreateTemplateOpen}
        onClose={() => setIsCreateTemplateOpen(false)}
        provider={cardProProviders}
        onTemplateSelected={onTemplateSelected}
      />
      <ModificationTemplate
        isOpen={isModificationTemplateOpen}
        onClose={() => setIsModificationTemplateOpen(false)}
        onTemplateSelected={onTemplateSelected}
      />

      <HardCodedTemplate
        isOpen={isHardcodedTemplateOpen}
        onClose={() => setIsHardcodedTemplateOpen(false)}
        onChange={onTemplateSelected}
      />

      <RoundedTopLeftContainer>
        <StyledTemplateSelector>
          <StyledTemplateSelectorLabel>
            {tFormData("formData.templateSelector.title")}
          </StyledTemplateSelectorLabel>
          <StyledTemplateSelectorButtons>
            <Button
              onClick={() => setIsCreateTemplateOpen(true)}
              variant='secondary'
              size='s'
              buttonText={tFormData("formData.templateSelector.buttons.create")}
            />
            <Button
              variant='secondary'
              size='s'
              buttonText={tFormData("formData.templateSelector.buttons.modify")}
              onClick={() => setIsModificationTemplateOpen(true)}
            />
            <Button
              variant='secondary'
              size='s'
              buttonText={tFormData(
                "formData.templateSelector.buttons.hardcoded",
              )}
              onClick={() => setIsHardcodedTemplateOpen(true)}
            />
            <StyledLoadButton
              variant='primary'
              type='submit'
              size='m'
              disabled={!json.length}
              onClick={onLoad}
              buttonText={tFormData("formData.templateSelector.buttons.load")}
            />
          </StyledTemplateSelectorButtons>
        </StyledTemplateSelector>
        <StyledPanelsContainer>
          <StyledJsonPanel>
            <ReactCodeMirror
              style={{ maxHeight: "100%", overflow: "auto" }}
              value={json}
              theme={githubLight}
              extensions={[jsonLanguage]}
              onChange={setJson}
            />
          </StyledJsonPanel>
          <StyledRenderPanel>
            <StyledTabMenuContainer>
              {tabs.map((tab: any, index: number) => (
                <TabMenuButton
                  content={tab}
                  key={`tab_${index}`}
                  index={index}
                  isselectedtab={index === selectedTab}
                  setTabSelected={() => setTabSelected(index)}
                  dataTestId='tab-menu-button'
                />
              ))}
            </StyledTabMenuContainer>

            {showRender && (
              <StyledRenderContainer>
                {formHasBeenLoaded ? (
                  <>
                    <StepperComponent />
                    <FormDataRenderer onSubmitted={handleSubmitFormdata} />
                  </>
                ) : (
                  <StyledEmptyForm>
                    {tFormData("formData.render.empty")}
                  </StyledEmptyForm>
                )}
              </StyledRenderContainer>
            )}
            {showAnomalies && <AnomaliesViewer />}
          </StyledRenderPanel>
        </StyledPanelsContainer>
      </RoundedTopLeftContainer>
    </StyledContainer>
  );
};

export default FormData;
